import {
  createContext, PropsWithChildren, useMemo, useState, useContext,
} from 'react';
import styled from 'styled-components';

export const BackDrop = styled.div`${({ theme }) => `
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  z-index: 1000;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
  box-sizing: border-box;
  background-color: ${theme.body};
`}`;

const IndicatorContainer = styled.div`${({ theme }) => `
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.padding[3]};
`}`;

const Indicator = styled.div`${({ theme }) => `
  width: 100px;
  height: ${theme.fontSize[1]};
  background-color: ${theme.secondary};
  border-radius: ${theme.padding[1]};

  animation: linear infinite alternate;
  animation-name: run;
  animation-duration: 0.8s;

  @keyframes run {
    0% {
      left: 0;
      transform: translateX(-100%);
    }
    100% {
      right: 0;
      transform: translateX(100%);
    }
  }
`}`;

export function LoadingSpinner() {
  return (
    <IndicatorContainer>
      <Indicator />
    </IndicatorContainer>
  );
}

type LoaderContextType = {
    loading: boolean,
    loaderCount: number,
    addLoader: () => void,
    removeLoader: () => void,
}

const defaultLoaderContext = {
  loading: false,
  loaderCount: 0,
  addLoader: () => {},
  removeLoader: () => {},
};

const loaderContext = createContext<LoaderContextType>(defaultLoaderContext);

export const useLoader = () => useContext(loaderContext);

export function LoaderProvider({ children }: PropsWithChildren) {
  const [loaderCount, setLoaderCount] = useState<number>(0);

  const addLoader = () => setLoaderCount((prev) => prev + 1);
  const removeLoader = () => setLoaderCount((prev) => prev - 1);

  const loading = useMemo(() => loaderCount > 0, [loaderCount]);

  const loaderValue = useMemo(() => ({
    loading, loaderCount, addLoader, removeLoader,
  }), [loading]);

  return (
    <loaderContext.Provider value={loaderValue}>
      <>
        { loading && (
          <BackDrop>
            <LoadingSpinner />
          </BackDrop>
        )}
        { children }
      </>
    </loaderContext.Provider>
  );
}
